import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { CellValueChangedEvent, GetRowIdParams } from 'ag-grid-enterprise'
import { Check } from 'lucide-react'
import { renderToString } from 'react-dom/server'

import { AgGridWidget, CustomMenuItem, createContextMenu } from '@fortem-monorepo/react-ag'
import { useApi, useUserContext } from '@fortem-monorepo/react-spa'
import { Audit, Trades } from '@fortem-monorepo/web-sdk'

import { useChangeActiveTradeMenuItem } from '@/ag-grid/context-menus/activate-trade'
import { createNewDBColDefs } from '@/pages/trading/booked-trades/grids/newdb/coldefs'
import { useApproveElementMutation } from '@/react-query/mutations/audit'
import { useUpdateTradeMutation } from '@/react-query/mutations/trades'
import { useInternalUser } from '@/react-query/queries/account'
import { useTradingData } from '@/react-query/queries/static'

export interface NewdbTradesGridProps {
  params: Trades.GetTradesParams
}

export const NewdbTradesGrid: React.FC<NewdbTradesGridProps> = ({ params }) => {
  const { internalUser } = useInternalUser()
  const userContext = useUserContext()
  const tradeData = useTradingData()

  const newdbColDefs = useMemo(
    () => createNewDBColDefs({ ctx: userContext, tradeData }),
    [userContext.user, tradeData],
  )

  const updateTradeMutation = useUpdateTradeMutation()
  const approveElementMutation = useApproveElementMutation()

  const api = useApi()

  const query = useQuery({
    queryKey: ['trades', 'newdb', params],
    queryFn: () => api.trades.getTrades(params),
    enabled: !!params?.st_dt,
  })

  const { Dialog: DeactivateTradeDialog, createMenuItems } =
    useChangeActiveTradeMenuItem<Trades.NewdbTrade>({
      mutation: updateTradeMutation,
    })

  const createNewDBContextMenu = () => {
    return createContextMenu<Trades.NewdbTrade>((params) => {
      const data = params.node?.data
      const status = data?.status
      const element_id = data?.trade_id!
      const statusIsNotApproved = status !== Audit.STATUSES.APPROVED
      const lastUpdatedByUser = internalUser?.user_id == data?.submitted_by

      const canApprove = statusIsNotApproved && !lastUpdatedByUser

      const defaultItems = params.defaultItems ?? []

      return [
        ...(canApprove
          ? ([
              {
                name: 'Approve Trade',
                icon: renderToString(<Check size={16} />),
                cssClasses: ['hover:bg-green-100!', 'text-green-600!'],
                action: () => {
                  return approveElementMutation.mutateAsync({
                    element_id,
                    element_type: Audit.ELEMENT_TYPES.TRADE,
                  })
                },
              },
              'separator',
            ] satisfies CustomMenuItem[])
          : []),
        ...createMenuItems(params),
        ...defaultItems,
      ]
    })
  }

  return (
    <>
      <DeactivateTradeDialog />
      <AgGridWidget
        widget={{ exposed: true, className: 'h-full' }}
        widgetHeader={{ title: 'Newdb Trades' }}
        query={query}
        grid={{
          defaultCsvExportParams: {
            allColumns: true,
          },
          getContextMenuItems: createNewDBContextMenu(),
          domLayout: 'normal',
          rowData: query.data,
          getRowId: (params: GetRowIdParams<Trades.NewdbTrade>) => params.data.trade_id,
          columnDefs: newdbColDefs,
          onCellValueChanged: (params: CellValueChangedEvent<Trades.NewdbTrade>) => {
            return updateTradeMutation.mutateAsync({
              rowData: params.data,
              updatedField: params.colDef.field,
              oldValue: params.oldValue ?? null,
              newValue: params.newValue,
            })
          },
        }}
      />
    </>
  )
}
